import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import {
  UserManager,
  UserManagerSettings,
  User,
  WebStorageStateStore,
  Log
} from 'oidc-client-ts';
import { AppSettingsService } from '../config/app-settings.service';
import { AutomationAuthService } from '../services/automation-auth.service';

@Injectable({ providedIn: 'root' })
export class OidcAuthService {
  private readonly _appSettings = inject(AppSettingsService);
  private readonly _automationAuthService = inject(AutomationAuthService);

  private readonly _loginChanged = signal<boolean>(false);
  loginChanged = toObservable(this._loginChanged);

  private manager!: UserManager;
  private user: User | null = null;
  private isLoginInProgress = false;
  private isAutomationUser = false;

  private baseApiUrl?: string;
  private readonly baseApiUrlIDMS?: string;
  private readonly baseApiUrlRDMS?: string;
  private isConfigurationLoaded = false;

  async loadConfiguration(): Promise<void> {
    this.baseApiUrl = this._appSettings.baseApiUrl;
    this.isConfigurationLoaded = true;

    const urlParams = new URLSearchParams(globalThis.location.search);
    const isAutomationMode = urlParams.has('automation');

    if (isAutomationMode) {
      console.log('[Automation] Automation mode detected in URL');
      await this.handleAutomationLogin();
      return;
    }

    const existingToken = sessionStorage.getItem('automation_token');
    const existingExpiry = sessionStorage.getItem('automation_token_expiry');

    if (existingToken && existingExpiry) {
      const expiryTime = new Date(existingExpiry).getTime();
      if (expiryTime > Date.now()) {
        console.log('[Automation] Using existing automation token from session');
        this.setAutomationUser();
        return;
      } else {
        console.log('[Automation] Token expired, clearing session');
        sessionStorage.removeItem('automation_token');
        sessionStorage.removeItem('automation_token_expiry');
        sessionStorage.removeItem('automation_username');
      }
    }

    const authority = this._appSettings.oidcConfiguration.authorityUrl;
    const clientId = this._appSettings.oidcConfiguration.AppIdentifier;
    const redirectUrl = this._appSettings.oidcConfiguration.redirectUrl;

    this.manager = new UserManager(getClientSettings(authority, clientId, redirectUrl));

    Log.setLogger(console);
    Log.setLevel(Log.INFO);

    //  Set up event handlers for token management
    this.manager.events.addAccessTokenExpiring(() => {
      // Token is about to expire, silent renewal will happen automatically
    });

    this.manager.events.addAccessTokenExpired(() => {
      this.user = null;
      this._loginChanged.set(false);
    });

    this.manager.events.addSilentRenewError(() => {
      // Silent renewal failed
    });

    this.manager.events.addUserLoaded((user) => {
      this.user = user;
      this._loginChanged.set(true);
    });

    this.manager.events.addUserUnloaded(() => {
      this.user = null;
      this._loginChanged.set(false);
    });

    //  Load user from storage on initialization
    this.user = await this.manager.getUser();

    // Only emit login status if user is valid
    if (this.user && !this.user.expired) {
      this._loginChanged.set(true);
    } else {
      this._loginChanged.set(false);
    }
  }

  isLoggedIn(): boolean {
    if (this.isAutomationUser) {
      const token = this.getAutomationToken();
      const expiry = sessionStorage.getItem('automation_token_expiry');
      if (!token || !expiry) return false;
      const expiryTime = new Date(expiry).getTime();
      return expiryTime > Date.now();
    }
    return !!this.user && !this.user.expired;
  }

  async login(): Promise<void> {
    //  Prevent multiple simultaneous login redirects
    if (this.isLoginInProgress) {
      return;
    }

    this.isLoginInProgress = true;
    await this.manager.signinRedirect({ state: globalThis.location.href });
  }

  getUserToken(): string {
    if (this.isAutomationUser) {
      return this.getAutomationToken() ?? '';
    }
    return this.user?.access_token ?? '';
  }

  get authorizationHeaderValue(): string {
    if (this.isAutomationUser) {
      const token = this.getAutomationToken();
      return token ? `Bearer ${token}` : '';
    }
    if (!this.user?.access_token) return '';
    return `Bearer ${this.user.access_token}`;
  }

  async completeAuthentication(): Promise<void> {
    const user = await this.manager.signinRedirectCallback();
    this.user = user;
    this.isLoginInProgress = false; // Reset login flag
    this._loginChanged.set(this.isLoggedIn());
    globalThis.history.replaceState({}, document.title, globalThis.location.origin + globalThis.location.pathname);
    globalThis.location.href = (user.state as string) || '/';
  }

  isUsingOIDC(): boolean {
    return this.isConfigurationLoaded;
  }

  isUrlProtected(request: string): boolean {
    return (
      this.isConfigurationLoaded &&
      (request.startsWith(this.baseApiUrl ?? '') ||
        request.startsWith(this.baseApiUrlIDMS ?? '') ||
        request.startsWith(this.baseApiUrlRDMS ?? ''))
    );
  }

  async signout(): Promise<void> {
    const redirectUrl = this._appSettings.oidcConfiguration.redirectUrl;
    const signedOutRoute = `${redirectUrl}/#/signed-out`;
    const currentLocation = globalThis.location.href;

      if (this.isAutomationUser) {
        await this.clearClientState();
        this.user = null;
        this.isAutomationUser = false;
        this.isLoginInProgress = false;
        this._loginChanged.set(false);
        globalThis.location.replace(signedOutRoute);
      return;
    }

      this.user = null;
      this.isLoginInProgress = false;
      this._loginChanged.set(false);

      if (this.manager) {
        await this.manager.signoutRedirect({
          post_logout_redirect_uri: `${redirectUrl}/auth-signout.html`,
          state: currentLocation
        });
        return;
      }

      await this.clearClientState();
      globalThis.location.replace(signedOutRoute);
  }

  async logoutToLogin(): Promise<void> {
    const redirectUrl = this._appSettings.oidcConfiguration.redirectUrl;
    const signInState = `${redirectUrl}/#/`;

    if (this.isAutomationUser) {
      await this.clearClientState();
      this.user = null;
      this.isAutomationUser = false;
      this.isLoginInProgress = false;
      this._loginChanged.set(false);
      globalThis.location.replace(signInState);
      return;
    }

    this.user = null;
    this.isLoginInProgress = false;
    this._loginChanged.set(false);

    if (this.manager) {
      await this.manager.removeUser();
      await this.manager.clearStaleState();
    }

    await this.clearClientState();

    if (this.manager) {
      this.isLoginInProgress = true;
      await this.manager.signinRedirect({
        state: signInState,
        prompt: 'login'
      });
      return;
    }

    globalThis.location.replace(signInState);
  }

  async clearClientState(): Promise<void> {
    try {
      globalThis.localStorage.clear();
    } catch (err) {
      console.warn('clearClientState: localStorage.clear failed', err);
    }
    try {
      globalThis.sessionStorage.clear();
    } catch (err) {
      console.warn('clearClientState: sessionStorage.clear failed', err);
    }
    try {
      this.clearCookies();
    } catch (err) {
      console.warn('clearClientState: clearCookies failed', err);
    }
    try {
      await this.clearCacheStorage();
    } catch (err) {
      console.warn('clearClientState: clearCacheStorage failed', err);
    }
  }

  async restoreUser(): Promise<void> {
    if (this.isAutomationUser) {
      this._loginChanged.set(this.isLoggedIn());
      return;
    }

    // If user is already loaded and valid, no need to restore
    if (this.user && !this.user.expired) {
      return;
    }

    try {
      const user = await this.manager.getUser();
      if (user && !user.expired) {
        this.user = user;
        this._loginChanged.set(true);
      } else {
        this.user = null;
        this._loginChanged.set(false);
      }
    } catch {
      this.user = null;
      this._loginChanged.set(false);
    }
  }

  private async handleAutomationLogin(): Promise<void> {
    const automationConfig = this._appSettings.automationAuthConfig;

    if (!automationConfig?.enabled) {
      alert('Automation authentication is not enabled.');
      globalThis.location.href = globalThis.location.origin + globalThis.location.pathname;
      return;
    }

    const encryptedEmail = prompt('Enter encrypted email:');
    if (!encryptedEmail) {
      alert('Encrypted email is required for automation login.');
      globalThis.location.href = globalThis.location.origin + globalThis.location.pathname;
      return;
    }

    if (!automationConfig.allowedEncryptedUsers.includes(encryptedEmail)) {
      alert('User is not authorized for automation authentication.');
      globalThis.location.href = globalThis.location.origin + globalThis.location.pathname;
      return;
    }

    try {
      const response = await firstValueFrom(
        this._automationAuthService.authenticate({ encryptedEmail })
      );

      const expiresInSeconds = Number(response.ExpiresIn);
      const expiryTime = new Date(Date.now() + (expiresInSeconds * 1000)).toISOString();
      sessionStorage.setItem('automation_token', response.Token);
      sessionStorage.setItem('automation_token_expiry', expiryTime);
      sessionStorage.setItem('automation_email', encryptedEmail);

      this.setAutomationUser();

      console.log('[Automation] Login successful, token stored');
      alert('Automation login successful!');

      globalThis.location.href = globalThis.location.origin + globalThis.location.pathname;
    } catch (error: any) {
      alert(`Automation login failed: ${error?.error?.message || error?.message || 'Unknown error'}`);
      globalThis.location.href = globalThis.location.origin + globalThis.location.pathname;
    }
  }

  private setAutomationUser(): void {
    this.isAutomationUser = true;
    this._loginChanged.set(true);
  }

  getAutomationToken(): string | null {
    if (!this.isAutomationUser) {
      return null;
    }
    return sessionStorage.getItem('automation_token');
  }

  isAutomationSession(): boolean {
    return this.isAutomationUser;
  }

  private async clearCacheStorage(): Promise<void> {
    if (!('caches' in globalThis)) {
      return;
    }

    const cacheKeys = await globalThis.caches.keys();
    await Promise.all(cacheKeys.map((cacheKey) => globalThis.caches.delete(cacheKey)));
  }

  private clearCookies(): void {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    const hostnameParts = globalThis.location.hostname.split('.');
    const domains = hostnameParts.map((_, index) => `.${hostnameParts.slice(index).join('.')}`);

    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim();
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      domains.forEach((domain) => {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
      });
    });
  }


}

export function getClientSettings(authority: string, clientId: string, redirectUrl: string): UserManagerSettings {
  return {
    authority,
    client_id: clientId,
    redirect_uri: `${redirectUrl}/auth-callback.html`,
    silent_redirect_uri: `${redirectUrl}/silent-callback.html`,
    post_logout_redirect_uri: `${redirectUrl}/auth-signout.html`,
    response_type: 'code',
    scope: 'openid profile email',
    automaticSilentRenew: true,
    filterProtocolClaims: false,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: globalThis.localStorage })
  };
}
